import { useState, useEffect, useRef } from 'react'
import { installFileAccess } from './lib/fileAccess'
import AdminMfa from './components/AdminMfa'
import { track } from './lib/track'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import './styles/global.css'

import Login from './pages/Login'
import Licences from './pages/Licences'
import Dashboard from './pages/Dashboard'
import Today from './pages/Today'
import { usePhone, isPhoneNow } from './hooks/usePhone'
import Chat from './pages/Chat'
import Admin from './pages/Admin'
import Fleet from './pages/Fleet'
import AppShell from './components/AppShell'
import Checklists from './pages/Checklists'
import ResetPassword from './pages/ResetPassword'
import Workspaces from './pages/Workspaces'
import RiskAssessments from './pages/RiskAssessments'
import Permits from './pages/Permits'
import Crew from './pages/Crew'
import Weather from './pages/Weather'
import Regulations from './pages/Regulations'
import PassagePlan from './pages/PassagePlan'
import Trips from './pages/Trips'
import ToDo from './pages/ToDo'
import LSA from './pages/LSA'
import CrewCertificates from './pages/CrewCertificates'
import Documents from './pages/Documents'
import DayLog from './pages/DayLog'
import EngineLog from './pages/EngineLog'
import RunningLog from './pages/RunningLog'
import Calendar from './pages/Calendar'
import HoursOfRest from './pages/HoursOfRest'
import Announcements from './pages/Announcements'
import LogBooks from './pages/LogBooks'
import Diary from './pages/Diary'
import RotationGrid from './pages/RotationGrid'
import DrillLog from './pages/DrillLog'
import DrillMatrix from './pages/DrillMatrix'
import DataTable from './components/DataTable'
import { TABLES } from './lib/tableConfig'
import WatchSchedulePage from './pages/WatchSchedule'
import OfficerReview from './pages/OfficerReview'
import Reports from './pages/Reports'
import Bunkering from './pages/Bunkering'
import OilRecordBook from './pages/OilRecordBook'
import AIActivity from './pages/AIActivity'
import ChangeHistory from './pages/ChangeHistory'
import Manuals from './pages/Manuals'
import Sops from './pages/Sops'
import { SOP_EXTRAS } from './components/SopExtras'
import Refit from './pages/Refit'
import Budget from './pages/Budget'
import DutyRota from './pages/DutyRota'
import CrewLogins from './pages/CrewLogins'
import MyDetails from './pages/MyDetails'
import PurchaseOrders from './pages/PurchaseOrders'
import APA from './pages/APA'
import { parseDeepLink, openKeys } from './lib/approvalsCore'
import IsmAudits from './pages/IsmAudits'
import SurveyReadiness from './pages/SurveyReadiness'
import Handover from './pages/Handover'
import FloatingAssistant from './components/FloatingAssistant'
import GuideHelpPanel from './components/GuideHelpPanel'
import TutorialHost from './components/TutorialHost'
import EmergencyLauncher from './components/EmergencyLauncher'
import { setEmergencyUser } from './lib/emergencyStore'
import OfflineBar from './components/OfflineBar'
import { setPageContext, aiAllowedFor } from './lib/appContext'
import Inbox from './comms/Inbox'   // Communication
import { useCommsUnread } from './comms/useCommsUnread'
import { CrewApp, useCrewHome, NoVessel } from './crew'
import EstimatePage from './service/EstimatePage'
import RequestPage from './service/RequestPage'
import TechnicianApp from './service/TechnicianApp'
import VesselJobs from './service/VesselJobs'
import { isSimple, SIMPLE_PAGES } from './service/simpleMode'

// link from an approval email (/?open=po&id=...&v=...): kept until after the login, then opened once (see ProtectedApp)
try { if (typeof window !== 'undefined' && parseDeepLink(window.location.search)) sessionStorage.setItem('hc_deeplink', window.location.search) } catch (e) {}

function ProtectedApp() {
  const { user, config, loading, can, hasFeature, accountType, selectedVessel, setSelectedVessel, signOut } = useAuth()
  const guard = (module, el) => ((can && !can(module,'read')) || (hasFeature && !hasFeature(module))) ? null : el
  const [page, setPage] = useState(() => {
    // phone / installed app: a fresh launch opens on Today; a reload keeps the page you were on
    try { if (isPhoneNow() && !sessionStorage.getItem('hc_launched')) { sessionStorage.setItem('hc_launched', '1'); return 'dashboard' } } catch (e) {}
    return localStorage.getItem('hc_page') || 'dashboard'
  })
  // the open page, for the floating assistant ("this page", "this pump" = the record the page has open)
  useEffect(() => { setPageContext(page) }, [page])
  // the emergency run on this device is offered to the assistant only for the login that started it
  useEffect(() => { setEmergencyUser(user && user.id, config && config.id) }, [user?.id, config?.id]) // eslint-disable-line
  // the assistant (floating button + Chat page): hidden for a login without AI access, as the Chat page
  const aiAllowed = aiAllowedFor({ accountType, permissions: config?.permissions, can, hasFeature })
  const phone = usePhone()
  useEffect(() => { if (user) track('page_view', page) }, [page, user])
  const [refreshKey, setRefreshKey] = useState(0)
  const [pageFilter, setPageFilterRaw] = useState('')
  // a filter set together with a page change (e.g. a dashboard card) applies to that page only;
  // any other page change (sidebar, links) clears it
  const pendingFilterRef = useRef(false)
  const setPageFilter = f => { pendingFilterRef.current = true; setPageFilterRaw(f) }
  useEffect(() => { if (pendingFilterRef.current) { pendingFilterRef.current = false; return } setPageFilterRaw('') }, [page]) // eslint-disable-line
  const [badges, setBadges] = useState({})
  // the Fleet view open at fleet level (Overview, Compliance ...): chosen in the left menu or by the hc-fleet-tab event (Getting started, tour, search). Starts at Overview, as ever, and again when a vessel is opened or left.
  const [fleetTab, setFleetTab] = useState('overview')
  useEffect(() => { const h = ev => { const t = ev && ev.detail && ev.detail.tab; if (t) setFleetTab(t) }; window.addEventListener('hc-fleet-tab', h); return () => window.removeEventListener('hc-fleet-tab', h) }, [])
  useEffect(() => { setFleetTab('overview') }, [selectedVessel?.id]) // eslint-disable-line
  const commsUnread = useCommsUnread(user && user.id)   // Communication: the Inbox badge
  // other pages can open a page: window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page } }))
  useEffect(() => {
    const go = ev => { const p = ev?.detail?.page; if (p) { window.__hcNavHandled = true; localStorage.setItem('hc_page', p); setPage(p) } }
    window.addEventListener('hc:navigate', go)
    return () => window.removeEventListener('hc:navigate', go)
  }, [])
  // a fleet answer's "Open →" (fleet briefing, src/pages/Chat.jsx): management opens that vessel on its Dashboard (fleet-1)
  useEffect(() => {
    const open = ev => {
      const id = ev?.detail?.id; if (!id || accountType !== 'company') return
      const v = (config?.fleet || []).find(x => String(x.id) === String(id)); if (!v) return
      setSelectedVessel(v); localStorage.setItem('hc_page', 'dashboard'); setPage('dashboard'); setRefreshKey(k => k + 1)
    }
    window.addEventListener('hc:open-vessel', open)
    return () => window.removeEventListener('hc:open-vessel', open)
  }, [accountType, config?.fleet]) // eslint-disable-line
  // approval email link: open that vessel (management) on that PO / APA, once
  useEffect(() => {
    if (!config) return
    let l = null
    try { l = parseDeepLink(sessionStorage.getItem('hc_deeplink') || ''); sessionStorage.removeItem('hc_deeplink') } catch (e) {}
    if (!l) return
    try { if (window.location.search) window.history.replaceState(null, '', window.location.pathname) } catch (e) {}
    const k = openKeys(l.kind, l.id, '', l)   // 'go': a related document of a risk assessment PDF
    try { localStorage.setItem(k.key, k.value); localStorage.setItem('hc_page', k.page); Object.entries(k.extra || {}).forEach(([a, b]) => localStorage.setItem(a, b)) } catch (e) {}
    if (accountType === 'company' && l.v) { const v = (config.fleet || []).find(x => String(x.id) === l.v); if (v) setSelectedVessel(v) }
    setPage(k.page); setRefreshKey(n => n + 1)
  }, [config?.id]) // eslint-disable-line
  const [timedOut, setTimedOut] = useState(false)
  // a crew member with no yacht login any more (turned off / removed) but a crew profile: the Crew Package, not an empty app
  const crewHome = useCrewHome({ user, config, loading })

  useEffect(() => {
    if (!loading) return
    const t = setTimeout(() => setTimedOut(true), 5000)
    return () => clearTimeout(t)
  }, [loading])

  function clearAndRetry() {
    localStorage.clear()
    sessionStorage.clear()
    window.location.href = '/login'
  }

  if (loading) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100vh',gap:16,color:'var(--muted)',fontSize:13}}>
      {!timedOut
        ? <><div className="spin" />Loading your vessel...</>
        : <>
            <div style={{fontSize:14,color:'var(--text)'}}>Taking too long to load...</div>
            <button onClick={clearAndRetry} style={{padding:'10px 24px',background:'var(--gold)',color:'var(--navy)',border:'none',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600}}>
              Clear session &amp; retry
            </button>
          </>
      }
    </div>
  )

  if (!user) return <Navigate to="/login" replace />
  if (crewHome === 'wait') return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}><div className="spin" /></div>
  if (crewHome === 'crew') return <Navigate to="/crew" replace />
  if (crewHome === 'none') return <NoVessel onSignOut={signOut} />

  // Service package: a technician has only their phone view
  if (config?.accountType === 'technician') return <TechnicianApp />

  // A company team member the company switched off (sql/fleet-4.sql): no vessel, no fleet - the database refuses everything anyway
  if (config?.accountType === 'locked') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1a2e', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '40px 44px', maxWidth: 440, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, color: '#15294a', marginBottom: 10 }}>Hey Captain</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#15294a', marginBottom: 8 }}>Your access was switched off</div>
          <div style={{ fontSize: 13.5, color: '#5a6b7d', lineHeight: 1.6, marginBottom: 22 }}>{config.companyName || 'Your management company'} has switched off this login. Ask them to switch it on again if you still need it.</div>
          <button onClick={signOut} style={{ background: '#15294a', color: '#fff', border: 0, borderRadius: 9, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>
    )
  }

  // Subscription status lockout: suspended/cancelled vessels are blocked entirely.
  // Admin accounts always bypass. Crew inherit their vessel's status.
  const subStatus = (config?.sub_status || 'active').toLowerCase()
  const isAdminAcct = config?.accountType === 'admin' || config?.is_admin
  if (config && !isAdminAcct && (subStatus === 'suspended' || subStatus === 'cancelled')) {
    const cancelled = subStatus === 'cancelled'
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1a2e', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '40px 44px', maxWidth: 440, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, color: '#15294a', marginBottom: 10 }}>Hey Captain</div>
          <div style={{ width: 46, height: 46, borderRadius: 23, background: cancelled ? '#fbe6e6' : '#fdf0d5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 22 }}>{cancelled ? '\u26d4' : '\u23f8\ufe0f'}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#15294a', marginBottom: 8 }}>
            {cancelled ? 'Subscription Ended' : 'Access Temporarily Suspended'}
          </div>
          <div style={{ fontSize: 13.5, color: '#5a6b7d', lineHeight: 1.6, marginBottom: 22 }}>
            {cancelled
              ? 'This vessel\'s subscription has ended and access is no longer active. Please contact us to reactivate your account.'
              : 'Access to this vessel has been temporarily suspended. This is usually due to a billing issue. Please contact us to restore access.'}
          </div>
          <div style={{ fontSize: 12, color: '#8a97a5', marginBottom: 20 }}>{config?.vessel_name}</div>
          <button onClick={signOut} style={{ background: '#15294a', color: '#fff', border: 0, borderRadius: 9, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>
    )
  }

  const PAGE_META = {
    dashboard: { title: 'Dashboard', subtitle: 'Vessel overview', showNew: false },
    chat:      { title: 'AI Assistant', subtitle: 'Your vessel assistant', showNew: false },
    inbox:     { title: 'Inbox', subtitle: 'Notices and messages', showNew: false },
    announcements: { title: 'Crew Board', subtitle: 'Notices, weather, tides & news', showNew: false },
    work_orders: { title: 'Jobs', subtitle: 'This vessel\'s jobs and visits', showNew: false },
    my_details: { title: 'My Details', subtitle: 'Your own contact details', showNew: false },
    equipment: { title: 'Equipment', subtitle: 'Systems & machinery', showNew: false },
    mlog:      { title: 'Maintenance Log', subtitle: 'Completed work records', showNew: false },
    mtask:     { title: 'Maintenance Tasks', subtitle: 'Scheduled & upcoming work', showNew: false },
    inventory: { title: 'Inventory', subtitle: 'Stores & consumables', showNew: false },
    purchase_orders: { title: 'Purchase Orders', subtitle: 'Suppliers, orders & deliveries', showNew: false },
    apa: { title: 'APA', subtitle: 'Charter allowance, receipts & statement', showNew: false },
    docs:      { title: 'Documents', subtitle: 'Ship certificates & records', showNew: false },
    checklists: { title: 'Checklists', subtitle: 'AI-powered procedures', showNew: false },
    todo: { title: 'To Do', subtitle: 'Personal & crew task lists', showNew: false },
    workspaces: { title: 'Workspaces', subtitle: 'Document knowledge bases', showNew: false },
    risk_assessments: { title: 'Risk Assessments', subtitle: 'Vessel risk assessments', showNew: false },
    permits: { title: 'Permits to Work', subtitle: 'Job card: brief the crew, check, sign, work', showNew: false },
    ism_audits: { title: 'ISM Audits', subtitle: 'Internal & external audits, non-conformities, close-out', showNew: false },
    survey_readiness: { title: 'Survey Readiness', subtitle: 'Flag, class, IOPP and PSC checklists \u00b7 filled from your records', showNew: false },
    refit: { title: 'Refit / Yard Period', subtitle: 'Refit project tracking and budget', showNew: false },
    budget: { title: 'Budget', subtitle: 'Annual operating budget & monthly actuals', showNew: false },
    crew: { title: 'Crew', subtitle: 'Vessel crew management', showNew: false },
    crew_logins: { title: 'Crew Logins', subtitle: 'Crew accounts & access', showNew: false },
    crew_certs: { title: 'Crew Certificates', subtitle: 'All crew certificates', showNew: false },
    day_log: { title: 'Day Log', subtitle: 'Watch entries', showNew: false },
    engine_log: { title: 'Engine Room Log', subtitle: 'Daily engineering entries', showNew: false },
    running_log: { title: 'Running Log', subtitle: 'Voyage entries', showNew: false },
    calendar: { title: 'Calendar', subtitle: config?.vessel_name, showNew: false },
    charters: { title: 'Charters', subtitle: 'Charter management', showNew: false },
    port_calls: { title: 'Port Calls', subtitle: 'Port arrivals & agents', showNew: false },
    weather: { title: 'Weather', subtitle: 'Marine forecast by port', showNew: false },
    regulations: { title: 'Regulations', subtitle: 'Maritime codes & guidance reference', showNew: false },
    passage_plan: { title: 'Passage Plans', subtitle: 'SOLAS V voyage planning', showNew: false },
    trips: { title: 'Trips', subtitle: 'Voyages \u00b7 auto-logged from AIS', showNew: false },
    marina_bookings: { title: 'Marina Bookings', subtitle: 'Berth reservations', showNew: false },
    hours_rest: { title: 'Hours of Rest', subtitle: 'MLC rest hours', showNew: false },
    watch_schedule: { title: 'Watch Schedule', subtitle: 'Roster planner \u00b7 MLC compliance', showNew: false },
    officer_review: { title: 'Officer Monthly Review', subtitle: 'Monthly compliance sign-off \u00b7 auto-filled from every module', showNew: false },
    ai_activity: { title: 'AI Activity', subtitle: 'AI usage, cost and every approved change', showNew: false },
    change_history: { title: 'Change History', subtitle: 'Who changed what, when \u00b7 every change, for ISM and inspections', showNew: false },
    reports: { title: 'Reports', subtitle: 'Owner & management reports \u00b7 generated from every module', showNew: false },
    manuals: { title: 'Manuals', subtitle: 'Equipment, owner\'s and SMS manuals - versions, approval, read receipts', showNew: false },
    sops: { title: 'SOPs', subtitle: 'Standard operating procedures - the Hey Captain standard, approved by the Master', showNew: false },
    duty_rota: { title: 'Duty Rota', subtitle: 'Who is on duty, day by day', showNew: false },
    logbooks: { title: 'Log Books', subtitle: '', showNew: false },
    diary: { title: "Ship's Diary", subtitle: 'Daily logbook & history', showNew: false },
    rotations: { title: 'Rotations', subtitle: 'Crew working & leave calendar', showNew: false },
    handover: { title: 'Handovers', subtitle: 'Rotation handovers \u00b7 written from the records, approved by the person going off', showNew: false },
  }

  const meta = page === 'dashboard' && phone ? { title: 'Today', subtitle: config?.vessel_name || '' }
    : page === 'dashboard_full' ? PAGE_META.dashboard
    : PAGE_META[page] || {}

  // Page -> feature id. Pages not listed are always allowed (dashboard, sub-pages).
  const PAGE_FEATURE = {
    chat: 'chat', announcements: 'announcements', checklists: 'checklists', todo: 'todo', apa: 'apa',
    lsa: 'lsa', risk_assessments: 'risk_assessments', permits: 'risk_assessments', ism_audits: 'ism_audits', survey_readiness: 'docs', refit: 'refit', budget: 'budget',
    crew: 'crew', crew_certs: 'crew', weather: 'weather', regulations: 'regulations', handover: 'rotations',
    passage_plan: 'passage_plan', trips: 'passage_plan', day_log: 'logbooks', engine_log: 'logbooks',
    running_log: 'logbooks', logbooks: 'logbooks', drill_log: 'checklists',
    drill_matrix: 'checklists', calendar: 'calendar', hours_rest: 'hours_rest',
    watch_schedule: 'watch_schedule', officer_review: 'officer_review', reports: 'reports', manuals: 'manuals', sops: 'manuals',
    duty_rota: 'duty_rota', equipment: 'equipment', mlog: 'mlog', mtask: 'mtask',
    inventory: 'inventory', purchase_orders: 'purchase_orders', docs: 'docs', charters: 'charters', port_calls: 'port_calls',
    marina_bookings: 'marina_bookings', workspaces: 'workspaces',
  }

  function renderPage() {
    // Company account with no vessel selected -> Fleet overview (the sub-admin layer above vessels).
    if (page === 'inbox') return <Inbox key={'inbox'+refreshKey} />   // Communication: also with no vessel open
    if (accountType === 'company' && !selectedVessel && page !== 'chat') {
      return <Fleet onOpenVessel={(v, p) => { setSelectedVessel(v); const pg = p || 'dashboard'; localStorage.setItem('hc_page', pg); setPage(pg); setRefreshKey(k => k + 1) }} tab={fleetTab} />
    }
    // Uniform feature gate: block any page whose feature the vessel doesn't have.
    const pf = PAGE_FEATURE[page]
    if (pf && hasFeature && !hasFeature(pf)) {
      return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>This module isn't included in your plan</div>
        <div style={{ fontSize: 13 }}>Contact your administrator to enable it.</div>
      </div>
    }
    if (page === 'dashboard' && phone) return <Today onNavigate={(p, f = '') => { setPageFilter(f); localStorage.setItem('hc_page', p); setPage(p) }} />
    if (page === 'dashboard' || page === 'dashboard_full') return <Dashboard onBadgesUpdate={setBadges} refreshKey={refreshKey} onNavigate={(p, f='') => { setPageFilter(f); setPage(p); }} />
    if (page === 'chat') return guard('ai', <Chat onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'announcements') return guard('announcements', <Announcements />)
    if (page === 'checklists') return guard('checklists', <Checklists onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'todo') return <ToDo />
    if (page === 'lsa') return <LSA onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'workspaces') return guard('workspaces', <Workspaces />)
    if (page === 'risk_assessments') return guard('risk', <RiskAssessments key={'ra'+refreshKey} onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'refit') return <Refit key={'refit'+refreshKey} />
    if (page === 'budget') return guard('budget', <Budget key={'budget'+refreshKey} />)
    if (page === 'permits') return guard('ptw', <Permits key={'ptw'+refreshKey} onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'purchase_orders') return guard('purchase_orders', <PurchaseOrders key={'po'+refreshKey} />)
    if (page === 'work_orders') return config?.service?.on ? guard('maintenance', <VesselJobs key={'wo'+refreshKey} />) : null   // Service package
    // Service package, simple mode: these pages do not open on this vessel (menu entries are hidden by can() / hasFeature())
    if (isSimple(config?.service, config?.id) && SIMPLE_PAGES.includes(page)) return null
    if (page === 'apa') return guard('apa', <APA key={'apa'+refreshKey} />)
    if (page === 'ism_audits') return guard('ism_audits', <IsmAudits key={'ism'+refreshKey} onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'survey_readiness') return guard('documents', <SurveyReadiness key={'sr'+refreshKey} onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'crew_logins') return accountType === 'crew' || config?.member ? null : <CrewLogins key={'cl'+refreshKey} />
    if (page === 'my_details') return <MyDetails />
    if (page === 'crew') return <Crew key="crew" onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'crew_certs') return <CrewCertificates key={'crew_certs'+pageFilter} initialFilter={pageFilter} onNavigate={(p, fl='') => { setPageFilter(fl); setPage(p); }} />
    if (page === 'weather') return guard('weather', <Weather />)
    if (page === 'regulations') return guard('regulations', <Regulations />)
    if (page === 'passage_plan') return guard('passage', <PassagePlan />)
    if (page === 'trips') return guard('passage', <Trips key={'trips'+refreshKey} onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'day_log') return <DayLog />
    if (page === 'engine_log') return <EngineLog />
    if (page === 'running_log') return <RunningLog />
    if (page === 'drill_log') return <DrillLog onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'drill_matrix') return <DrillMatrix key={'dm'+pageFilter} initialFilter={pageFilter} onNavigate={(p, fl='') => { setPageFilter(fl); localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'calendar') return <Calendar key={'cal'+pageFilter} initialFilter={pageFilter} onNavigate={(p) => { setPageFilter(''); localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'hours_rest') return <HoursOfRest onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'watch_schedule') return <WatchSchedulePage onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />
    if (page === 'officer_review') return <OfficerReview />
    if (page === 'ai_activity') return (accountType === 'admin' || config?.is_admin) ? <AIActivity /> : null
    if (page === 'reports') return <Reports />
    if (page === 'change_history') return <ChangeHistory key={'ch'+refreshKey} />
    if (page === 'manuals') return guard('manuals', <Manuals />)
    if (page === 'sops') return guard('manuals', <Sops key={'sops' + refreshKey} extras={SOP_EXTRAS} />)
    if (page === 'duty_rota') return <DutyRota />
    if (page === 'logbooks') return guard('logbooks', <LogBooks onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }} />)
    if (page === 'diary') return guard('diary', <Diary />)
    if (page === 'bunkering') return guard('logbooks', <Bunkering />)
    if (page === 'oil_record_book') return guard('logbooks', <OilRecordBook />)
    if (page === 'rotations') return guard('rotations', <RotationGrid />)
    if (page === 'handover') return guard('rotations', <Handover key={'ho'+refreshKey} />)
    if (page === 'docs') { const iq = (() => { const q = localStorage.getItem('hc_open_doc'); if (q) { localStorage.removeItem('hc_open_doc'); return q } return '' })() || takeOpenQuery('docs'); return <Documents key={'docs'+pageFilter} initialFilter={pageFilter} initialQuery={iq} /> }
    const tbl = TABLES[page]
    if (tbl) {
      const iq = page === 'docs' ? (() => { const q = localStorage.getItem('hc_open_doc'); if (q) { localStorage.removeItem('hc_open_doc'); return q } return '' })()
        : page === 'mtask' ? (() => { const q = localStorage.getItem('hc_open_mtask'); if (q) { localStorage.removeItem('hc_open_mtask'); return q } return '' })()
        : page === 'charters' ? (() => { const q = localStorage.getItem('hc_open_charter'); if (q) { localStorage.removeItem('hc_open_charter'); return q } return '' })()
        : page === 'port_calls' ? (() => { const q = localStorage.getItem('hc_open_portcall'); if (q) { localStorage.removeItem('hc_open_portcall'); return q } return '' })()
        : page === 'marina_bookings' ? (() => { const q = localStorage.getItem('hc_open_marina'); if (q) { localStorage.removeItem('hc_open_marina'); return q } return '' })()
        : ''
      const op = iq ? { q: iq, id: null } : takeOpen(page)   // a record clicked under an AI answer (Chat SourceLinks) or found by the search
      const oq = op.q
      return (
      <DataTable
        tableKey={tbl.tableKey}
        columns={tbl.columns}
        formFields={tbl.formFields}
        title={tbl.title}
        key={page + pageFilter}
        initialFilter={pageFilter}
        initialQuery={oq}
        openId={op.id}
      />
    )}
    return null
  }

  return (
    <AppShell
      page={page}
      onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p); }}
      fleetTab={fleetTab}
      onFleetTab={setFleetTab}
      badges={{ ...badges, comms: commsUnread.notices + commsUnread.messages }}
      title={meta.title}
      subtitle={meta.subtitle}
      showNew={meta.showNew}
      onRefresh={page !== 'chat' ? () => { setRefreshKey(k => k + 1); setPage(p => p) } : undefined}
    >
      {accountType === 'company' && selectedVessel && (
        // the fleet's navy carried into the vessel (fleet-1): the bar keeps the Fleet page's colours, the page fades in below it
        <div className="hc-fleet-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', margin: '0 0 4px', background: '#0a1322', color: '#e7edf6', borderBottom: '1px solid rgba(255,255,255,.08)', flexWrap: 'wrap' }}>
          <button onClick={() => setSelectedVessel(null)} style={{ background: 'none', border: '1px solid #3b7bff', color: '#9bbcff', fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 7, cursor: 'pointer' }}>&larr; Back to fleet</button>
          <span style={{ fontSize: 12.5, color: '#8fa1bb' }}>Viewing</span>
          <select value={selectedVessel.id} onChange={e => { const v = (config?.fleet || []).find(x => x.id === e.target.value); if (v) { setSelectedVessel(v); setPage('dashboard'); setRefreshKey(k => k + 1) } }}
            style={{ background: '#101c31', border: '1px solid rgba(255,255,255,.14)', borderRadius: 7, padding: '5px 10px', color: '#e7edf6', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {(config?.fleet || []).map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
          <span style={{ fontSize: 12.5, color: '#8fa1bb' }}>&middot; {config?.companyName} &middot; Fleet Manager</span>
        </div>
      )}
      <OfflineBar uid={user && user.id} />
      {accountType === 'company' && page !== 'chat'
        ? <div key={selectedVessel ? selectedVessel.id : 'fleet'} className="hc-switch-in">{renderPage()}</div>
        : renderPage()}
      <GuideHelpPanel aiAllowed={aiAllowed} />
      <TutorialHost page={page} aiAllowed={aiAllowed} onHome={() => { if (accountType === 'company') setSelectedVessel(null); localStorage.setItem('hc_page', 'dashboard'); setPage('dashboard') }} />
      <FloatingAssistant page={page} aiAllowed={aiAllowed} onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p) }} />
      <EmergencyLauncher onNavigate={(p) => { localStorage.setItem('hc_page', p); setPage(p) }} />
    </AppShell>
  )
}

// a record clicked under an AI answer or found by the header search: { page, q, id } -> that page opens filtered to the
// record (once); with an id the list page also opens the record itself
function takeOpen(page) {
  try {
    const o = JSON.parse(localStorage.getItem('hc_open_q') || 'null')
    if (!o || o.page !== page) return { q: '', id: null }
    localStorage.removeItem('hc_open_q')
    return { q: String(o.q || ''), id: o.id ? String(o.id) : null }
  } catch (e) { return { q: '', id: null } }
}
const takeOpenQuery = page => takeOpen(page).q

// Admin page: only for the admin account. Everyone else is sent away before the page renders.
function AdminGate() {
  const { user, config, loading } = useAuth()
  if (loading || (user && !config)) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  const isAdmin = config?.accountType === 'admin' || !!config?.is_admin
  if (!isAdmin) return <Navigate to="/" replace />
  return <AdminMfa><Admin /></AdminMfa>
}

installFileAccess()

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/licences" element={<Licences />} />
          <Route path="/admin" element={<AdminGate />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/crew/*" element={<CrewApp />} />
          <Route path="/estimate" element={<EstimatePage />} />
          <Route path="/request" element={<RequestPage />} />
          <Route path="/*" element={<ProtectedApp />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
// test
